import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'relocation_cookie_consent_v1';

function readConsent(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'accepted';
  } catch {
    return false;
  }
}

export function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!readConsent()) {
      // Lazy mount: avoid hydration/SSR mismatch + give the user a beat before showing
      const id = window.setTimeout(() => setVisible(true), 600);
      return () => window.clearTimeout(id);
    }
  }, []);

  if (!visible) return null;

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'accepted');
    } catch {
      // ignore
    }
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-labelledby="cookie-banner-title"
      data-testid="cookie-banner"
      // Banner box constraints (DO NOT REGRESS — user reported these issues
      // multiple times):
      //   - `px-4 pt-4`  : horizontal + top padding
      //   - `pb-[max(...)]`  : bottom padding = MAX(1.25rem, iOS safe-area).
      //     Earlier we used `pb-5 pb-safe` together — `pb-safe` (which
      //     resolves to env(safe-area-inset-bottom)) was emitted later in
      //     the cascade and OVERRODE the explicit 20px, collapsing
      //     bottom-padding to 0 on non-iOS and making the CTA row touch
      //     the banner edge. The calc keeps a 20px floor regardless.
      //   - The "Got it" button below stays at `px-2.5 py-1 text-xs` so the
      //     button height is line-height-of-copy, not a chunky CTA.
      // Covered by:
      //   web/src/components/layout/__tests__/CookieBanner.test.tsx (unit)
      //   tools/test_cookie_visual.py (Playwright pixel-level gap check)
      // — fix any test failures there before relaxing these tokens.
      className="fixed inset-x-2 bottom-2 z-[1200] max-w-md rounded-lg border border-slate-200 bg-white px-4 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl sm:left-4 sm:right-auto sm:inset-x-auto"
    >
      <div id="cookie-banner-title" className="mb-1 text-sm font-semibold text-slate-800">
        🍪 {t('gdpr.bannerTitle')}
      </div>
      <p className="mb-4 text-xs text-slate-600">{t('gdpr.bannerBody')}</p>
      <div className="flex items-center justify-between gap-3 text-xs">
        <div className="flex min-w-0 flex-wrap gap-x-3 gap-y-1">
          <Link to="/privacy" className="text-blue-600 hover:underline">
            {t('gdpr.privacy')}
          </Link>
          <span className="text-slate-300">·</span>
          <Link to="/terms" className="text-blue-600 hover:underline">
            {t('gdpr.terms')}
          </Link>
        </div>
        <button
          type="button"
          onClick={accept}
          data-testid="cookie-accept"
          // Sized to match surrounding text-xs links so the banner height is
          // driven by the copy, not by a chunky CTA. User had to ask multiple
          // times — DON'T bump back up to px-4 py-2 / text-sm.
          className="inline-flex shrink-0 items-center justify-center rounded-md bg-[color:var(--color-terra-600)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-paper)] shadow-sm hover:bg-[color:var(--color-terra-700)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-terra-600)]"
        >
          {t('gdpr.accept')}
        </button>
      </div>
    </div>
  );
}
